import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AppThunk } from '../store'
import {
  getSystem,
  patchSystem,
  patchUrlDetailStatus,
  getMailDelivery,
  patchMailDelivery,
  getMenuDisplay,
  patchMenuDisplay,
  getCustomAlertList,
  postCustomAlert,
  patchCustomAlert,
  deleteCustomAlert,
  patchCustomAlertStatus,
  deleteAdministrator,
  getAdministratorList,
  patchAdministrator,
  postAdministrator,
  getCompanyService,
  getMfaQrCode,
  patchMfaAuth,
  deleteMfaAuth,
  getSlackChannelList,
  getIpRestrictionList,
  patchIpRestrictionList,
  getOfficeIpList,
  patchOfficeIpList,
  getAllowAccess,
  getSettingPersonal,
  getSettingCommon,
  getSettingMeasureRestriction,
  patchSettingMeasureRestriction,
  getSettingWebBrowser,
  patchSettingWebBrowser,
  getSettingWeeklyTrackTime,
  patchSettingWeeklyTrackTime,
  getSettingRole,
  getSettingRoleDetail,
  postSettingRole,
  patchSettingRole,
  getSettingTeam,
  getSettingTeamDetail,
  patchSettingTeam,
  postSettingTeam,
  deleteSettingTeam,
  patchSettingTeamManager,
  getSettingBaseWorkTime,
  patchSettingBaseWorkTime,
  getSettingHoliday,
  getSettingHolidayDetail,
  postSettingHoliday,
  deleteSettingRole,
  getSettingMember,
  getSettingCustomAlert,
  postSettingCustomAlert,
  patchSettingCustomAlert,
  deleteSettingCustomAlert,
  postSettingMember,
  patchSettingMember,
  deleteSettingMember,
} from '../../apis/setting'
import { setErrors } from '../common/errorsSlice'
import { updateCommonMfaAuth, deleteCommonMfaAuth } from '../common/commonPartSlice'
import {
  System,
  RecordData,
  UrlDetailStatusRequest,
  MailDelivery,
  MenuDisplay,
  getCustomAlertListResponse,
  postCustomAlertRequest,
  patchCustomAlertRequest,
  deleteCustomAlertRequest,
  patchCustomAlertForStatusRequest,
  deleteAdministratorRequest,
  patchAdministratorRequest,
  postAdministratorRequest,
  getAdministratorListResponse,
  CompanyService,
  patchMfaAuthRequest,
  SlackChannelList,
  IpRestrictionList,
  patchIpRestrictionListRequest,
  OfficeIpList,
  patchOfficeIpListRequest,
  responseGetSettingPersonal,
  responseGetSettingCommon,
  ResponseGetSettingMeasureRestriction,
  RequestPatchSettingMeasureRestriction,
  ResponseGetSettingWebBrowser,
  RequestPatchSettingWebBrowser,
  ResponseGetSettingWeeklyTrackTime,
  RequestPatchSettingWeeklyTrackTime,
  RequestGetSettingWeeklyTrackTime,
  ResponseGetSettingRole,
  RequestGetSettingRoleDetail,
  ResponseGetSettingRoleDetail,
  ResponsePostSettingRole,
  RequestPatchSettingRole,
  ResponseGetSettingTeam,
  RequestGetSettingTeamDetail,
  ResponseGetSettingTeamDetail,
  RequestPatchSettingTeam,
  RequestPostSettingTeam,
  ResponsePostSettingTeam,
  RequestDeleteSettingTeam,
  RequestPatchSettingTeamManager,
  ResponseGetSettingBaseWorkTime,
  RequestPatchSettingBaseWorkTime,
  ResponseGetSettingHoliday,
  RequestGetSettingHoliday,
  RequestGetSettingHolidayDetail,
  ResponseGetSettingHolidayDetail,
  RequestPostSettingHoliday,
  RequestDeleteSettingRole,
  ResponseGetSettingMember,
  ResponseGetSettingCustomAlert,
  ResponsePostSettingCustomAlert,
  RequestPostSettingCustomAlert,
  RequestPatchSettingCustomAlert,
  RequestDeleteSettingCustomAlert,
  RequestPostSettingMember,
  RequestPatchSettingMember,
} from '../../types/setting'

export interface SystemState extends System {
  isLoading: boolean
}

const initialState: Partial<SystemState> = {
  google_account: null,
  microsoft_account: null,
  cybozu_account: null,
  is_available_cybozu: false,
  mail_delivery: null,
  menu_display: null,
  custom_alert_list: null,
  administrator_list: null,
  company_service: null,
  mfa_qr_code_uri: "",
  slack_channel_list: null,
  ip_restriction_list: null,
  office_ip_list: null,
  isLoading: true,
}

const systemSlice = createSlice({
  name: 'system',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    getSystemSuccess: (state, action: PayloadAction<System>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      state.isLoading = false
      return state
    },
    patchSystemSuccess: (state, action: PayloadAction<RecordData>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      state.isLoading = false
      return state
    },
    patchUrlDetailStatusSuccess: (state, action: PayloadAction<UrlDetailStatusRequest>) => {
      // eslint-disable-next-line no-param-reassign
      state = { ...state, ...action.payload }
      state.isLoading = false
      return state
    },
    getMailDeliverySuccess: (state, action: PayloadAction<MailDelivery>) => {
      state.mail_delivery = action.payload
      state.isLoading = false
      return state
    },
    patchMailDeliverySuccess: (state, action: PayloadAction<MailDelivery>) => {
      state.mail_delivery = action.payload
      state.isLoading = false
      return state
    },
    getMenuDisplaySuccess: (state, action: PayloadAction<MenuDisplay>) => {
      state.menu_display = action.payload.visible
      state.isLoading = false
      return state
    },
    patchMenuDisplaySuccess: (state, action: PayloadAction<MenuDisplay>) => {
      state.menu_display = action.payload.visible
      state.isLoading = false
      return state
    },
    getCustomAlertListSuccess: (state, action: PayloadAction<getCustomAlertListResponse>) => {
      state.custom_alert_list = action.payload
      return state
    },
    getAdministratorListSuccess: (state, action: PayloadAction<getAdministratorListResponse>) => {
      state.administrator_list = action.payload.admin_list
      return state
    },
    getCompanyServiceSuccess: (state, action: PayloadAction<CompanyService>) => {
      state.company_service = action.payload
      return state
    },
    getSlackChannelListSuccess: (state, action: PayloadAction<SlackChannelList[]>) => {
      state.slack_channel_list = action.payload
      return state
    },
    getIpRestrictionListSuccess: (state, action: PayloadAction<IpRestrictionList[]>) => {
      state.ip_restriction_list = action.payload
      state.isLoading = false
      return state
    },
    getOfficeIpListSuccess: (state, action: PayloadAction<OfficeIpList[]>) => {
      state.office_ip_list = action.payload
      state.isLoading = false
      return state
    },
    requestFailure: (state) => {
      state.isLoading = false
      return state
    },
  },
})

export const {
  requestStart,
  getSystemSuccess,
  patchSystemSuccess,
  getMailDeliverySuccess,
  patchMailDeliverySuccess,
  patchUrlDetailStatusSuccess,
  getMenuDisplaySuccess,
  patchMenuDisplaySuccess,
  getCustomAlertListSuccess,
  getAdministratorListSuccess,
  getCompanyServiceSuccess,
  getSlackChannelListSuccess,
  getIpRestrictionListSuccess,
  getOfficeIpListSuccess,
  requestFailure,
} = systemSlice.actions

export const systemReducer = systemSlice.reducer

export const fetchSystem = (
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getSystem()
    .then((result) => {
      dispatch(getSystemSuccess(result))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateSystem = (
  params: RecordData,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchSystem(params)
    .then((result) => {
      dispatch(patchSystemSuccess(result))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateUrlStatus = (
  params: UrlDetailStatusRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchUrlDetailStatus(params)
    .then(() => {
      dispatch(patchUrlDetailStatusSuccess(params))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchMailDelivery = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getMailDelivery()
    .then((result) => {
      dispatch(getMailDeliverySuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateMailDelivery = (
  params: MailDelivery,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchMailDelivery(params)
    .then((result) => {
      dispatch(patchMailDeliverySuccess(result))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchMenuDisplay = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getMenuDisplay()
    .then((result) => {
      dispatch(getMenuDisplaySuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateMenuDisplay = (
  params: MenuDisplay,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchMenuDisplay(params)
    .then((result) => {
      dispatch(patchMenuDisplaySuccess(result))
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchCustomAlertList = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getCustomAlertList()
    .then((result) => {
      dispatch(getCustomAlertListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const createCustomAlert = (
  params: postCustomAlertRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  postCustomAlert(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateCustomAlert = (
  params: patchCustomAlertRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  patchCustomAlert(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateCustomAlertStatus = (
  params: patchCustomAlertForStatusRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  patchCustomAlertStatus(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const delCustomAlert = (
  params: deleteCustomAlertRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  deleteCustomAlert(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchAdministratorList = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getAdministratorList()
    .then((result) => {
      dispatch(getAdministratorListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const createAdministrator = (
  params: postAdministratorRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  postAdministrator(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateAdministrator = (
  params: patchAdministratorRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  patchAdministrator(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const delAdministrator = (
  params: deleteAdministratorRequest,
  callback: () => void,
): AppThunk => async (dispatch) => {
  deleteAdministrator(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchCompanyService = (
): AppThunk => async (dispatch) => {
  getCompanyService()
    .then((result) => {
      dispatch(getCompanyServiceSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchMfaQrCode = (
  callback: (result: {url: string, google_secret: string}) => void,
): AppThunk => async (dispatch) => {
  getMfaQrCode()
    .then((result) => {
      callback(result)
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateMfaAuth = (
  params: patchMfaAuthRequest,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  patchMfaAuth(params)
    .then(() => {
      dispatch(updateCommonMfaAuth())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
      errorCallback()
    })
}

export const delMfaAuth = (
  callback: () => void,
): AppThunk => async (dispatch) => {
  deleteMfaAuth()
    .then(() => {
      dispatch(deleteCommonMfaAuth())
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchSlackChannelList = (
  callback?: (result: SlackChannelList[]) => void,
): AppThunk => async (dispatch) => {
  getSlackChannelList()
    .then((result) => {
      dispatch(getSlackChannelListSuccess(result))
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchAllowAccess = (
  failedCallback: () => void,
): AppThunk => async (dispatch) => {
  getAllowAccess()
    .then()
    .catch(() => {
      dispatch(requestFailure())
      failedCallback()
    })
}

export const fetchIpRestrictionList = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getIpRestrictionList()
    .then((result) => {
      dispatch(getIpRestrictionListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateIpRestrictionList = (
  params: patchIpRestrictionListRequest,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  patchIpRestrictionList(params)
    .then(() => {
      callback()
    })
    .catch(() => {
      dispatch(requestFailure())
      errorCallback()
    })
}

export const fetchOfficeIpList = (
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getOfficeIpList()
    .then((result) => {
      dispatch(getOfficeIpListSuccess(result))
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateOfficeIpList = (
  params: patchOfficeIpListRequest,
  callback: () => void,
  errorCallback: () => void,
): AppThunk => async (dispatch) => {
  patchOfficeIpList(params)
    .then(() => {
      callback()
    })
    .catch(() => {
      dispatch(requestFailure())
      errorCallback()
    })
}

export const fetchSettingPersonal = (
  callback?: (result: responseGetSettingPersonal) => void,
): AppThunk => async (dispatch) => {
  getSettingPersonal()
    .then((result: responseGetSettingPersonal) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingCommon = (
  callback?: (result: responseGetSettingCommon) => void,
): AppThunk => async (dispatch) => {
  getSettingCommon()
    .then((result: responseGetSettingCommon) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingMeasureRestriction = (
  callback?: (result: ResponseGetSettingMeasureRestriction) => void,
): AppThunk => async (dispatch) => {
  getSettingMeasureRestriction()
    .then((result: ResponseGetSettingMeasureRestriction) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const updateSettingMeasureRestriction = (
  params: RequestPatchSettingMeasureRestriction,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  patchSettingMeasureRestriction(params)
    .then(() => {
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingWebBrowser = (
  callback?: (result: ResponseGetSettingWebBrowser) => void,
): AppThunk => async (dispatch) => {
  getSettingWebBrowser()
    .then((result: ResponseGetSettingWebBrowser) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const updateSettingWebBrowser = (
  params: RequestPatchSettingWebBrowser,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  patchSettingWebBrowser(params)
    .then(() => {
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingWeeklyTrackTime = (
  params: RequestGetSettingWeeklyTrackTime,
  callback?: (result: ResponseGetSettingWeeklyTrackTime) => void,
): AppThunk => async (dispatch) => {
  getSettingWeeklyTrackTime(params)
    .then((result: ResponseGetSettingWeeklyTrackTime) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const updateSettingWeeklyTrackTime = (
  params: RequestPatchSettingWeeklyTrackTime,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  patchSettingWeeklyTrackTime(params)
    .then(() => {
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingRole = (
  callback?: (result: ResponseGetSettingRole) => void,
): AppThunk => async (dispatch) => {
  getSettingRole()
    .then((result: ResponseGetSettingRole) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingRoleDetail = (
  params: RequestGetSettingRoleDetail,
  callback?: (result: ResponseGetSettingRoleDetail) => void,
): AppThunk => async (dispatch) => {
  getSettingRoleDetail(params)
    .then((result: ResponseGetSettingRoleDetail) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const createSettingRole = (
  callback?: (result: ResponsePostSettingRole) => void,
): AppThunk => async (dispatch) => {
  postSettingRole()
    .then((result: ResponsePostSettingRole) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const updateSettingRole = (
  params: RequestPatchSettingRole,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  patchSettingRole(params)
    .then(() => {
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const delSettingRole = (
  params: RequestDeleteSettingRole,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  deleteSettingRole(params)
    .then(() => {
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingTeam = (
  callback?: (result: ResponseGetSettingTeam) => void,
): AppThunk => async (dispatch) => {
  getSettingTeam()
    .then((result: ResponseGetSettingTeam) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingTeamDetail = (
  params: RequestGetSettingTeamDetail,
  callback?: (result: ResponseGetSettingTeamDetail) => void,
): AppThunk => async (dispatch) => {
  getSettingTeamDetail(params)
    .then((result: ResponseGetSettingTeamDetail) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const createSettingTeam = (
  params: RequestPostSettingTeam,
  callback?: (result: ResponsePostSettingTeam) => void,
): AppThunk => async (dispatch) => {
  postSettingTeam(params)
    .then((result: ResponsePostSettingTeam) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const updateSettingTeam = (
  params: RequestPatchSettingTeam,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  patchSettingTeam(params)
    .then(() => {
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const delSettingTeam = (
  params: RequestDeleteSettingTeam,
  callback: () => void,
): AppThunk => async (dispatch) => {
  deleteSettingTeam(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateSettingTeamManager = (
  params: RequestPatchSettingTeamManager,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  patchSettingTeamManager(params)
    .then(() => {
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingMember = (
  callback?: (result: ResponseGetSettingMember) => void,
): AppThunk => async (dispatch) => {
  getSettingMember()
    .then((result: ResponseGetSettingMember) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const createSettingMember = (
  postParams: RequestPostSettingMember,
  callback: () => void,
  errorCallback?: () => void,
): AppThunk => async (dispatch) => {
  postSettingMember(postParams)
    .then(() => {
      callback()
    })
    .catch(() => {
      dispatch(requestFailure())
      if (errorCallback) errorCallback()
    })
}

export const updateSettingMember = (
  memberId: number,
  params: RequestPatchSettingMember,
  callback: () => void,
  errorCallback?: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchSettingMember(memberId, params)
    .then(() => {
      callback()
    })
    .catch(() => {
      if (errorCallback) errorCallback()
    })
}

export const destroySettingMember = (
  memberId: number,
  callback: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  deleteSettingMember(memberId)
    .then(() => {
      callback()
    })
    .catch(() => {
      dispatch(requestFailure())
    })
}

export const fetchSettingBaseWorkTime = (
  callback?: (result: ResponseGetSettingBaseWorkTime) => void,
): AppThunk => async (dispatch) => {
  getSettingBaseWorkTime()
    .then((result: ResponseGetSettingBaseWorkTime) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const updateSettingBaseWorkTime = (
  params: RequestPatchSettingBaseWorkTime,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  patchSettingBaseWorkTime(params)
    .then(() => {
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingHoliday = (
  params: RequestGetSettingHoliday,
  callback?: (result: ResponseGetSettingHoliday) => void,
): AppThunk => async (dispatch) => {
  getSettingHoliday(params)
    .then((result: ResponseGetSettingHoliday) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingHolidayDetail = (
  params: RequestGetSettingHolidayDetail,
  callback?: (result: ResponseGetSettingHolidayDetail) => void,
): AppThunk => async (dispatch) => {
  getSettingHolidayDetail(params)
    .then((result: ResponseGetSettingHolidayDetail) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const createSettingHoliday = (
  params: RequestPostSettingHoliday,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  postSettingHoliday(params)
    .then(() => {
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(setErrors(error))
    })
}

export const fetchSettingCustomAlert = (
  callback?: (result: ResponseGetSettingCustomAlert) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getSettingCustomAlert()
    .then((result: ResponseGetSettingCustomAlert) => {
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const createSettingCustomAlert = (
  params: RequestPostSettingCustomAlert,
  callback: (result: ResponsePostSettingCustomAlert) => void,
): AppThunk => async (dispatch) => {
  postSettingCustomAlert(params)
    .then((result: ResponsePostSettingCustomAlert) => {
      callback(result)
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateSettingCustomAlert = (
  params: RequestPatchSettingCustomAlert,
  callback: () => void,
): AppThunk => async (dispatch) => {
  patchSettingCustomAlert(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const delSettingCustomAlert = (
  params: RequestDeleteSettingCustomAlert,
  callback: () => void,
): AppThunk => async (dispatch) => {
  deleteSettingCustomAlert(params)
    .then(() => {
      callback()
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}
